import React, { useState, useMemo } from "react";
import {
  useGetStagingTemplateDetailQuery,
  useGetProdTemplateDetailQuery,
} from "../../../../app/api/console/scanTemplateSyncApiSlice";
import { useGetAllConsoleWebsitesQuery } from "../../../../app/api/console/consoleWebsitesApiSlice";
import { ScanModeBadge } from "./badges";

const DiffRow = ({ label, stagingVal, prodVal, isPrompt }) => {
  const [expanded, setExpanded] = useState(false);
  const changed = stagingVal !== prodVal;
  if (!changed) return null;

  return (
    <div className="border border-amber-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-amber-50">
        <span className="text-sm font-medium text-amber-900">{label}</span>
        {isPrompt && (
          <button onClick={() => setExpanded(!expanded)} className="text-xs text-amber-700 hover:text-amber-900 underline">
            {expanded ? "Collapse" : "Show full text"}
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 divide-x text-xs">
        <div className="p-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-500">Staging</span>
          <div className="mt-1 text-gray-800 font-mono whitespace-pre-wrap">
            {isPrompt && !expanded ? (stagingVal ? `${stagingVal.slice(0, 120)}${stagingVal.length > 120 ? "..." : ""}` : "(empty)") : (stagingVal || "(empty)")}
          </div>
        </div>
        <div className="p-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-orange-500">Production</span>
          <div className="mt-1 text-gray-800 font-mono whitespace-pre-wrap">
            {isPrompt && !expanded ? (prodVal ? `${prodVal.slice(0, 120)}${prodVal.length > 120 ? "..." : ""}` : "(empty)") : (prodVal || "(empty)")}
          </div>
        </div>
      </div>
      {isPrompt && (
        <div className="grid grid-cols-2 divide-x border-t text-[10px] text-gray-400">
          <div className="px-2 py-1">{stagingVal?.length || 0} chars</div>
          <div className="px-2 py-1">{prodVal?.length || 0} chars</div>
        </div>
      )}
    </div>
  );
};

const SyncDiffPanel = ({ templateId, syncStatus }) => {
  const [collapsed, setCollapsed] = useState(false);

  const { data: stagingDetail } = useGetStagingTemplateDetailQuery({ templateId });
  const { data: prodDetail } = useGetProdTemplateDetailQuery({ templateId });
  const { data: consoleWebsites } = useGetAllConsoleWebsitesQuery();

  const { stagingToProd, websiteNames } = useMemo(() => {
    const s2p = {};
    const names = {};
    if (consoleWebsites) {
      for (const w of consoleWebsites) {
        if (w.staging_website_id && w.prod_website_id) {
          s2p[w.staging_website_id] = w.prod_website_id;
        }
        if (w.staging_website_id) names[w.staging_website_id] = w.name;
        if (w.prod_website_id) names[w.prod_website_id] = w.name;
      }
    }
    return { stagingToProd: s2p, websiteNames: names };
  }, [consoleWebsites]);

  if (!syncStatus) return null;
  if (syncStatus.in_sync) return null;

  if (!syncStatus.staging_exists) {
    return (
      <div className="mb-5 px-4 py-3 bg-yellow-50 border border-yellow-300 rounded-lg flex items-center gap-3">
        <div className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />
        <p className="text-sm text-yellow-800">This template <strong>only exists in production</strong>. Pull to staging to sync.</p>
      </div>
    );
  }
  if (!syncStatus.prod_exists) {
    return (
      <div className="mb-5 px-4 py-3 bg-blue-50 border border-blue-300 rounded-lg flex items-center gap-3">
        <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
        <p className="text-sm text-blue-800">This template <strong>only exists in staging</strong>. Push to prod to publish.</p>
      </div>
    );
  }

  const loading = !stagingDetail || !prodDetail;

  const diffs = [];
  if (stagingDetail && prodDetail) {
    if (stagingDetail.scan_mode !== prodDetail.scan_mode) {
      diffs.push({ key: "scan_mode", label: "Scan Mode", staging: stagingDetail.scan_mode, prod: prodDetail.scan_mode, isBadge: true });
    }
    if (stagingDetail.plan_type !== prodDetail.plan_type) {
      diffs.push({ key: "plan_type", label: "Plan Type", staging: stagingDetail.plan_type, prod: prodDetail.plan_type });
    }
    if (stagingDetail.title !== prodDetail.title) {
      diffs.push({ key: "title", label: "Title", staging: stagingDetail.title, prod: prodDetail.title });
    }
    if (stagingDetail.description !== prodDetail.description) {
      diffs.push({ key: "description", label: "Description", staging: stagingDetail.description || "", prod: prodDetail.description || "" });
    }
    if (stagingDetail.automation_ready !== prodDetail.automation_ready) {
      diffs.push({ key: "automation_ready", label: "Automation Ready", staging: String(stagingDetail.automation_ready), prod: String(prodDetail.automation_ready) });
    }
    if (stagingDetail.requires_file !== prodDetail.requires_file) {
      diffs.push({ key: "requires_file", label: "Requires File", staging: String(stagingDetail.requires_file), prod: String(prodDetail.requires_file) });
    }
    if (stagingDetail.requires_scope !== prodDetail.requires_scope) {
      diffs.push({ key: "requires_scope", label: "Requires Scope", staging: String(stagingDetail.requires_scope ?? false), prod: String(prodDetail.requires_scope ?? false) });
    }

    const sBaseText = stagingDetail.base_prompt?.current_prompt_text || "";
    const pBaseText = prodDetail.base_prompt?.current_prompt_text || "";
    if (sBaseText !== pBaseText) {
      diffs.push({ key: "base_prompt", label: "Base Prompt", staging: sBaseText, prod: pBaseText, isPrompt: true });
    }

    const sWP = stagingDetail.website_prompts || [];
    const pWP = prodDetail.website_prompts || [];
    const prodById = {};
    for (const pp of pWP) { prodById[pp.website_id] = pp; }

    const matchedProdIds = new Set();
    for (const sp of sWP) {
      const mappedId = stagingToProd[sp.website_id] || sp.website_id;
      const pp = prodById[mappedId];
      const wName = websiteNames[sp.website_id] || websiteNames[mappedId] || mappedId;
      if (pp) {
        matchedProdIds.add(pp.website_id);
        const sText = sp.current_prompt_text || "";
        const pText = pp.current_prompt_text || "";
        if (sText !== pText) {
          diffs.push({ key: `wp_${sp.website_id}`, label: `Website Prompt (${wName})`, staging: sText || "(empty)", prod: pText || "(empty)", isPrompt: true });
        }
      } else {
        const sText = sp.current_prompt_text || "";
        diffs.push({ key: `wp_${sp.website_id}`, label: `Website Prompt (${wName})`, staging: sText || "(empty)", prod: "(does not exist)", isPrompt: true });
      }
    }
    for (const pp of pWP) {
      if (!matchedProdIds.has(pp.website_id)) {
        const pText = pp.current_prompt_text || "";
        const wName = websiteNames[pp.website_id] || pp.website_id;
        diffs.push({ key: `wp_${pp.website_id}`, label: `Website Prompt (${wName})`, staging: "(does not exist)", prod: pText || "(empty)", isPrompt: true });
      }
    }

    const sContainers = stagingDetail.test_cases_containers || [];
    const pContainers = prodDetail.test_cases_containers || [];
    const prodTcByWebsite = {};
    for (const tc of pContainers) { prodTcByWebsite[tc.website_id || "__base__"] = tc; }

    const matchedProdTcKeys = new Set();
    for (const sc of sContainers) {
      const key = sc.website_id || "__base__";
      const mappedKey = sc.website_id ? (stagingToProd[sc.website_id] || sc.website_id) : "__base__";
      const pc = prodTcByWebsite[mappedKey];
      const tcLabel = sc.website_id ? `Test Cases (${websiteNames[sc.website_id] || websiteNames[mappedKey] || sc.website_id})` : "Test Cases (Base)";
      const sCases = sc.current_test_cases || [];
      const sJson = JSON.stringify(sc.current_test_cases || []);
      if (pc) {
        matchedProdTcKeys.add(mappedKey);
        const pJson = JSON.stringify(pc.current_test_cases || []);
        const pCases = pc.current_test_cases || [];
        if (sJson !== pJson) {
          diffs.push({ key: `tc_${key}`, label: tcLabel, staging: `${sCases.length} test case${sCases.length !== 1 ? "s" : ""}`, prod: `${pCases.length} test case${pCases.length !== 1 ? "s" : ""}` });
        }
      } else {
        diffs.push({ key: `tc_${key}`, label: tcLabel, staging: `${sCases.length} test case${sCases.length !== 1 ? "s" : ""}`, prod: "(does not exist)" });
      }
    }
    for (const pc of pContainers) {
      const key = pc.website_id || "__base__";
      if (!matchedProdTcKeys.has(key)) {
        const pCases = pc.current_test_cases || [];
        const tcLabel = pc.website_id ? `Test Cases (${websiteNames[pc.website_id] || pc.website_id})` : "Test Cases (Base)";
        diffs.push({ key: `tc_${key}`, label: tcLabel, staging: "(does not exist)", prod: `${pCases.length} test case${pCases.length !== 1 ? "s" : ""}` });
      }
    }
  }

  return (
    <div className="mb-5 border-2 border-amber-300 rounded-xl overflow-hidden bg-amber-50/50">
      <div
        className="flex items-center justify-between px-4 py-3 bg-amber-100 cursor-pointer hover:bg-amber-200/70 transition-colors"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
          <h4 className="text-sm font-semibold text-amber-900">Out of Sync</h4>
          {!loading && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-200 text-amber-800">
              {diffs.length} difference{diffs.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!loading && !collapsed && diffs.some(d => d.key === "scan_mode") && (
            <span className="text-xs text-amber-700">
              Mode: <ScanModeBadge mode={diffs.find(d => d.key === "scan_mode").staging} />
              {" "}vs{" "}
              <ScanModeBadge mode={diffs.find(d => d.key === "scan_mode").prod} />
            </span>
          )}
          <svg className={`w-4 h-4 text-amber-600 transition-transform ${collapsed ? "" : "rotate-180"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {!collapsed && (
        <div className="p-4 space-y-3">
          {loading ? (
            <div className="text-center py-4 text-sm text-amber-600">Loading comparison...</div>
          ) : diffs.length === 0 ? (
            <div className="text-center py-4 text-sm text-amber-600">Could not detect specific differences (data may still be loading)</div>
          ) : (
            diffs.map((diff) => (
              <DiffRow
                key={diff.key}
                label={diff.label}
                stagingVal={diff.staging}
                prodVal={diff.prod}
                isPrompt={diff.isPrompt}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SyncDiffPanel;
